/* eslint-disable @typescript-eslint/naming-convention */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { HotToastService } from '@ngneat/hot-toast';
import { BadgeOptions } from '../../../components/badge/badge.interface';
import { DataCollectorService } from '../../../core/services/data_collector_services/data_collector_service.service';
import { LocalisationService } from '../../../core/services/localisation/zuper.localisation.service';
import { UtilityService } from '../../../core/services/utility_functions/utility_functions.service';
import { PickerSelectOptions } from '../data-picker.interface';
import _ from 'lodash';
import { FormGroup } from '@angular/forms';

@Component({
  selector: 'contract-data-picker',
  templateUrl: './contract-data-picker.component.html',
  styleUrls: ['./contract-data-picker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContractDataPickerComponent implements OnInit, OnChanges {
  @Input() filter: any = null;
  @Input() selectOption: PickerSelectOptions = PickerSelectOptions.select;
  @Output() eventEmitter: EventEmitter<any> = new EventEmitter();
  @Input() selectedContracts: any[] = [];
  @Input() searchKey: string = '';
  @Input() pickerConfig: any = null;
  @Input() contractUrl: any = '';
  @Input() pageIndex: number = 0;
  @Input() showSelectedItems: boolean = false;
  @Input() sortBy: string = 'created_at';
  @Input() sortType: string = 'DESC';
  @Input() customParams: any = null;
  @Input() filterFormGroup: FormGroup;
  @Input() hasFilterValue: boolean = false;

  contracts: any[] = [];
  contractsList: any[] = [];
  loaded: boolean = false;
  defaultParamObj: any = {
    'sort': this.sortType,
    'sort_by': this.sortBy,
    'filter.is_active': true
  };
  contractEleId: string;
  contractDetails: any = null;
  onSearch: boolean = true;
  localeKeys: any = {
    expand: 'expand',
    collapse: 'collapse',
    pending: 'text.pending',
    onHold: 'text.on_hold',
    approved: 'text.approved',
    rejected: 'text.rejected',
    customerApproval: 'text.await_customer_approval',
    customerRejected: 'text.customer_rejected',
    customerApproved: 'text.customer_approved',
  };
  isSpinLoading: boolean = false;

  constructor(
    private utilityService: UtilityService,
    private dataCollectorService: DataCollectorService,
    private localisationService: LocalisationService,
    private changeDetectorRef: ChangeDetectorRef,
    private toast: HotToastService) { }

  ngOnInit(): void {
    this.localisationService.setDefaultLanguage();
    if (this.selectOption && this.selectOption === PickerSelectOptions.readOnly) {
      delete this.defaultParamObj?.['filter.is_active'];
    }
    this.filterFormGroup?.valueChanges.subscribe((data: any) => {
      this.updateDataParams(data);
    });
    this.showDataPickerPanel();
  }

  rotateArrowIcon(contractUID: any): any {
    return {
      'rotate-arrow-icon-down': this.isExpandingAction(contractUID),
      'rotate-arrow-icon-up': !this.isExpandingAction(contractUID)
    };
  }

  showSpinLoading(contractUID: any): boolean {
    return this.isExpandingAction(contractUID) && this.isSpinLoading;
  }

  updateArrowTooltipText(contractUID: any): string {
    return this.isExpandingAction(contractUID) ? this.localeKeys.collapse : this.localeKeys.expand;
  }

  isExpandingAction(contractUID: any): boolean {
    return (this.contractEleId === 'contract_details_' + contractUID);
  }

  viewContractFullDetails(contractUID: string): string {
    return '/contracts/' + contractUID + '/details';
  }

  viewContractDetails(event: any, contractUID: string): void {
    event.stopPropagation();
    const elementId: string = 'contract_details_' + contractUID;
    if (this.contractEleId === elementId) {
      this.resetContractDetails();
    } else {
      this.contractEleId = elementId;
      this.getContractDetails(contractUID);
    }
  }

  getStatusBadgeOptions(type: string, displayName: string): BadgeOptions {
    const badgeOption: BadgeOptions = {
      type: type,
      displayType: type,
      displayKey: null,
      displayName: displayName ?? type
    };
    return badgeOption;
  }

  getContractDetails(contractUID: string): void {
    this.isSpinLoading = true;
    /* eslint-disable quotes */
    this.dataCollectorService.getData(this.contractUrl + `/` + contractUID).subscribe(
      /* eslint-enable quotes */
      (contracts: any) => {
        this.contractDetails = contracts.data;
        this.isSpinLoading = false;
        this.changeDetectorRef.detectChanges();
      }, (error: any) => {
        this.resetContractDetails();
        this.isSpinLoading = false;
        this.changeDetectorRef.detectChanges();
      });
  }

  resetContractDetails(): void {
    this.contractEleId = '';
    this.contractDetails = null;
  }

  isSelectedContract(contract: any): boolean {
    if (this.selectedContracts?.length) {
      return this.selectedContracts.find(value => value.contract_uid === contract.contract_uid) ? true : false;
    }
    return false;
  }

  selectContract(event: any, contract: any): void {
    if (this.selectOption === PickerSelectOptions.multiSelect) {
      if (event.checked === true) {
        this.selectedContracts.push(contract);
      } else {
        this.selectedContracts = this.selectedContracts.filter(value => value.contract_uid !== contract.contract_uid);
      }
    } else {
      this.selectedContracts = [contract];
    }
    this.eventEmitter.emit({ value: this.selectedContracts });
  }

  triggerSearch(searchKey: any): void {
    delete this.defaultParamObj[this.pickerConfig.params.uniqueSearchKey];
    this.onSearch = true;
    if (this.defaultParamObj[this.pickerConfig.params.searchKey] !== searchKey) {
      const pageIndex: number = 0;
      this.defaultParamObj[this.pickerConfig.params.searchKey] = searchKey;
      this.getData(pageIndex);
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
  }

  sortingAction(key: string, value: string): void {
    if (value) {
      const pageIndex: number = this.pageIndex??0;
      this.defaultParamObj[key] = value;
      this.getData(pageIndex);
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
  }

  getData(pageIndex: number, isOpen?: boolean): void {
    this.contractEleId = '';
    this.loaded = false;
    this.defaultParamObj.page = pageIndex + 1;
    const urlParams = this.utilityService.constructParam(this.defaultParamObj);

    this.dataCollectorService.getData(this.contractUrl, urlParams).subscribe(
      (response: any) => {
        this.contractsList = response.data;
        this.contracts = response.data;
        this.loaded = true;
        this.eventEmitter.emit({ totalRecords: response.total_records });
        if (isOpen && this.filter && this.contracts.length) {
          this.selectedContracts = this.selectedContracts?.length > 0 ? _.uniqBy([...this.selectedContracts, ...this.filter], 'contract_uid') : this.filter;
          this.eventEmitter.emit({ value: this.selectedContracts });
        } else if (isOpen && this.contracts.length === 0) {
          this.onSearch = false;
        }
        this.changeDetectorRef.detectChanges();
      }, (error: any) => {
        this.loaded = true;
        this.changeDetectorRef.detectChanges();
      });
  }

  showDataPickerContent(): void {
    if (this.hasFilterValue) {
      return null;
    }
    const pageIndex: number = this.pageIndex??0;
    Object.keys(this.pickerConfig.params).forEach((key: any) => {
      const paramKey: any = this.pickerConfig.params[key];
      if (key !== 'uniqueSearchKey') {
        let value: any = '';
        if (key === 'countKey') {
          value = 10;
        } else if (key === 'pageKey') {
          value = 1;
        } else if (key === 'searchKey' && !this.showSelectedItems && this.searchKey) {
          value = this.searchKey;
        }
        this.defaultParamObj[paramKey] = value;
      }
    });
    if (this.filter) {
      this.updateFilterValue();
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
    if (this.customParams && Object.keys(this.customParams).length) {
      this.defaultParamObj = { ...this.defaultParamObj, ...this.customParams };
    }
    this.eventEmitter.emit({ sortBy: this.defaultParamObj['sort_by'], sortType: this.defaultParamObj['sort'] });
    this.getData(pageIndex, true);
  }

  updateFilterValue(): void {
    if (this.selectOption === PickerSelectOptions.select && this.filter.length > 0) {
      if (this.filter[0].contract_number) {
        this.defaultParamObj[this.pickerConfig.params.searchKey] = this.filter[0].contract_number;
        this.eventEmitter.emit({ searchKey: this.filter[0].contract_number });
      }
      else {
        this.defaultParamObj[this.pickerConfig.params.uniqueSearchKey] = this.filter[0].contract_uid;
      }
    }
  }

  updateDataParams(formControlValues: any): void {
    if (this.customParams && Object.keys(this.customParams).length) {
      this.defaultParamObj = { ...this.defaultParamObj, ...this.customParams };
    }
    const params: any = this.defaultParamObj ?? {};
    if (formControlValues.moduleBasedFilterValue) {
      let hasModuleFilterValue: boolean = false;
      formControlValues.moduleBasedFilterValue.forEach((value: any) => {
        if (value.value) {
          params[value?.key] = value.value;
          hasModuleFilterValue = true;
        } else {
          delete params[value?.key];
        }
      });
    }
    this.updateFilters();
  }

  updateFilters(): void {
    const pageIndex: number = 0;
    this.getData(pageIndex, true);
    this.eventEmitter.emit({ pageIndex: pageIndex });
  }

  resetContractDataPicker(): void {
    this.contracts = [];
  }

  showDataPickerPanel(): void {
    this.resetContractDataPicker();
    if (this.showSelectedItems) {
      this.contracts = this.selectedContracts;
    } else if (this.contractsList.length === 0 && this.searchKey) {
      this.showDataPickerContent();
    } else if (this.contractsList.length > 0 || this.searchKey) {
      this.contracts = this.contractsList;
    } else {
      this.showDataPickerContent();
    }
  }

  hideSelectedItemsPanel(): void {
    this.eventEmitter.emit({ showSelectedItems: false, value: this.selectedContracts, filter: this.selectedContracts });
  }

  getBadgeOptions(): BadgeOptions {
    return {
      type: 'PRIMARY',
      displayKey: null,
      displayName: this.selectedContracts ? this.selectedContracts.length : 0,
      displayType: 'listingCount'
    };
  }

  getApprovalStatusOptions(approvalStatus: string): BadgeOptions {
    return {
      type: approvalStatus,
      displayKey: this.getApprovalStatusLocaleKey(approvalStatus),
      displayName: approvalStatus.replaceAll('_', ' ')
    };
  }

  getApprovalStatusLocaleKey(approvalStatus: string): string {
    let localeKey: string = '';
    switch (approvalStatus) {
      case 'AWAIT_APPROVAL':
        localeKey = this.localeKeys.pending;
        break;
      case 'ON_HOLD':
        localeKey = this.localeKeys.onHold;
        break;
      case 'APPROVED':
        localeKey = this.localeKeys.approved;
        break;
      case 'REJECTED':
        localeKey = this.localeKeys.rejected;
        break;
      case 'AWAIT_CUSTOMER_APPROVAL':
        localeKey = this.localeKeys.customerApproval;
        break;
      case 'CUSTOMER_REJECTED':
        localeKey = this.localeKeys.customerRejected;
        break;
      case 'CUSTOMER_APPROVED':
        localeKey = this.localeKeys.customerApproved;
        break;
    }
    return localeKey;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.searchKey?.firstChange === false) {
      this.triggerSearch(changes?.searchKey?.currentValue);
    }

    if (changes?.pageIndex?.previousValue !== undefined) {
      this.getData(changes?.pageIndex?.currentValue);
    }

    if (changes?.showSelectedItems?.firstChange === false) {
      this.showDataPickerPanel();
    }

    if (changes?.sortBy?.firstChange === false && changes?.sortBy?.previousValue !== undefined) {
      this.sortingAction('sort_by', changes?.sortBy?.currentValue);
    }

    if (changes?.sortType?.firstChange === false && changes?.sortType?.previousValue !== undefined) {
      this.sortingAction('sort', changes?.sortType?.currentValue);
    }
  }
}
